import axios from 'axios';
import qs from 'qs';
import i18next from 'i18next';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { store } from 'store/index';
import { SET_USER, SET_SEGMENTATIONS, SET_PRPOERTIES, SET_CONDITIONS, LOGOUT } from 'store/actions';
import { initialState } from 'store/sessionReducer';
import { msalInstance } from 'App';
import { apiRequest } from '../authConfig';

// MSAL BrowserAuthError codes raised when the browser blocks the popup window
const POPUP_BLOCKED_ERROR_CODES = ['popup_window_error', 'empty_window_error'];

export const isPopupBlockedError = (error) => POPUP_BLOCKED_ERROR_CODES.includes(error?.errorCode);

// Raised when the user closes the popup before finishing the sign in
export const isPopupClosedError = (error) => error?.errorCode === 'user_cancelled';

/**
 * Clears the session so App renders the Login page, showing the given message.
 */
const endSession = (errorMessage) => {
    store.dispatch({ type: LOGOUT });
    store.dispatch({ type: SET_USER, ...initialState, user: undefined, username: '', errorLogin: true, errorMessage });
};

// Shared interactive request so concurrent API calls don't open several popups
let interactiveTokenRequest = null;

/**
 * Renews the token interactively. Uses a popup and falls back to a full page redirect
 * when the browser blocks it. If the user closes the popup the session is ended and
 * the Login page is shown with a message.
 */
const acquireTokenInteractive = (account) => {
    if (!interactiveTokenRequest) {
        interactiveTokenRequest = msalInstance
            .acquireTokenPopup({ ...apiRequest, account })
            .then((result) => result.accessToken)
            .catch(async (err) => {
                if (isPopupBlockedError(err)) {
                    await msalInstance.acquireTokenRedirect({ ...apiRequest, account });
                    // The page navigates away; keep the request pending until it does
                    return new Promise(() => {});
                }
                endSession(isPopupClosedError(err) ? i18next.t('login:popupClosed') : i18next.t('login:sessionExpired'));
                throw err;
            })
            .finally(() => {
                interactiveTokenRequest = null;
            });
    }
    return interactiveTokenRequest;
};

/**
 * Acquires a fresh B2C access token for the Web API scope using MSAL silent flow.
 * @param {object|null} account - MSAL account to use. When omitted the active account
 *   is resolved from the Redux session (msalHomeAccountId); falls back to accounts[0]
 *   as a last resort so legacy / non-MSAL flows keep working.
 */
const acquireBearerToken = async (account = null) => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        // 1. Use explicitly passed account, or look up by homeAccountId stored in Redux.
        const homeAccountId = store.getState().session.msalHomeAccountId;
        const resolvedAccount = account
            ?? (homeAccountId ? msalInstance.getAccountByHomeId(homeAccountId) : null)
            ?? accounts[0];

        try {
            const result = await msalInstance.acquireTokenSilent({
                ...apiRequest,
                account: resolvedAccount
            });
            return result.accessToken;
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                // Token cannot be renewed silently – force a new interactive login
                return acquireTokenInteractive(resolvedAccount);
            }
            // Fall through to legacy token
        }
    }
    // Fallback: use the internal legacy token stored after the token-exchange
    return store.getState().session.accessToken;
};

// request interceptor – attaches a valid bearer token to every outgoing request
axios.interceptors.request.use(
    async (config) => {
        //const token = await acquireBearerToken();
        // If the caller already set an Authorization header (e.g. loginWithMsal passing the
        // selected account's idToken), preserve it. Otherwise acquire one via MSAL silent flow.
        const existingAuth = config.headers?.Authorization;
        let authHeader = existingAuth ?? null;

        if (!authHeader || authHeader == 'Bearer') {
            // Resolve the active MSAL account from Redux so the correct user's token is used.
            const homeAccountId = store.getState().session.msalHomeAccountId;
            const account = homeAccountId ? msalInstance.getAccountByHomeId(homeAccountId) : null;
            const token = await acquireBearerToken(account);
            authHeader = token ? `Bearer ${token}` : null;
        }

        const selectedCompany = store.getState().session.selectedCompany;
        const username = store.getState().session.username;
        var companyAssociationId = '0';
        var selectedCompanyId = '0';
        if (selectedCompany) {
            companyAssociationId = selectedCompany.companyAssociationId;
            selectedCompanyId = selectedCompany.wholesalerId;
        }
        if (authHeader) {
            config.headers = {
                'Content-Type': 'application/json',
                Authorization: authHeader,
                CompanyAssociationId: companyAssociationId,
                SelectedCompanyId: selectedCompanyId,
                UserName: username
            };
        }
        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use((response) => response, (error) => {
    console.log(error)
    // No response: the request never left (e.g. token acquisition failed in the request interceptor)
    if (!error?.response) {
        return Promise.reject(error);
    }
    if (error.response.status === 401) {
        if (error?.config?.url !== '/api/Account/token') {
            store.dispatch({ type: LOGOUT });
            window.location = '/';
        }
        else {
            store.dispatch({ type: SET_USER, ...initialState, errorLogin: true, errorMessage: error.response?.data?.Errors?.BusinessException });
        }
    }
    else if (error.response.status !== 200) {
        if (error.response?.status === 400 && error.response?.data) {
            var message = ''
            Object.entries(error.response.data).map((value) => {
                message += value[1] + '|'
            });
            throw new Error(message)
        }
        else if (error?.response?.data?.errors?.businessException) {
            throw new Error(error?.response?.data?.errors?.businessException);
        }
        else if (error?.response?.data?.error) {
            // e.g. { message: "An error occurred...", error: "El usuario ingresado no cuenta con empresas asociadas." }
            throw new Error(error.response.data.error);
        }
        else {
            throw new Error(error?.response?.data?.message || error?.message || error);
        }
    }
});


/**
 * Login with MSAL - This method is called after MSAL authentication
 * @param {object} msalAccount - The account object from MSAL
 * @param {string} accessToken - The access token from MSAL (Azure AD B2C token)
 */
const loginWithMsal = async (msalAccount, accessToken) => {
    let username = "";
    try {
        // Exchange Azure AD B2C token for application token
        // Send the B2C token to your backend to validate and get your app's token
        // B2C accounts: username may be empty; prefer idTokenClaims values
        const claims = msalAccount.idTokenClaims || {};
        username = msalAccount.username
            || (claims.emails && claims.emails[0])
            || claims.email
            || claims.preferred_username
            || '';
        const name = msalAccount.name || claims.name || claims.given_name || username;
        //print username and name from msal account and claims
         console.log('MSAL Account:', msalAccount);
         console.log('ID Token Claims:', claims);
        // console.log('Username:', username);
        // console.log('Name:', name);

        var config = {
            method: 'post',
            url: '/api/Account/token/msal',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: JSON.stringify({
                azureAdToken: accessToken,
                username: username,
                name: name
            })
        };

        const response = await axios(config);
        const user = response?.data;

        if (user) {
            store.dispatch({ type: SET_USER, user: user, username: username, msalHomeAccountId: msalAccount.homeAccountId });
            const info = await getInfoRules(user.accessToken);
            if (info) {
                store.dispatch({ type: SET_SEGMENTATIONS, segmentations: info?.segmentations });
                store.dispatch({ type: SET_PRPOERTIES, properties: info?.properties });
                store.dispatch({ type: SET_CONDITIONS, conditions: info?.conditions });
            }
        }

        return { success: true, user };
    } catch (error) {
        console.log(error);
        const resMessage = (error.response && error.response.data && (error.response.data.error || error.response.data.message)) || error.message || error.toString();
        store.dispatch({ type: SET_USER, user: undefined, username: username, errorLogin: true, errorMessage: resMessage });
        return { success: false, error: resMessage };
    }
};

/**
 * Legacy login method - kept for backward compatibility
 */
const login = async (username, password) => {
    var data = qs.stringify({
        userName: username,
        password: password
    });

    var config = {
        method: 'post',
        url: '/api/Account/token',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: data
    };

    await axios(config)
        .then((response) => {
            const user = response?.data;
            console.log(user)
            if (user) {
                store.dispatch({ type: SET_USER, user: user, username: username });
                getInfoRules(user.accessToken)?.then((info) => {
                    store.dispatch({ type: SET_SEGMENTATIONS, segmentations: info?.segmentations });
                    store.dispatch({ type: SET_PRPOERTIES, properties: info?.properties });
                    store.dispatch({ type: SET_CONDITIONS, conditions: info?.conditions });
                })
            }
        })
        .catch((error) => {
            console.log(error)
            const resMessage = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            store.dispatch({ type: SET_USER, user: undefined, username: username, errorLogin: true, errorMessage: resMessage });
        });
};

const logout = () => {

    var config = {
        method: 'get',
        url: '/api/Account/logout'
    };
    return axios(config)
        .then(function (response) {
            return response.data;
        })
        .catch(function (error) {
            throw new Error(error)
        });
};

const getInfoRules = async (token) => {

    const headers = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            CompanyAssociationId: '0',
            SelectedCompanyId: '0'
        }
    }

    try {
        const responseSegmentations = await axios.get('/api/Rules/segmentaciones', headers);
        const responseProperties = await axios.get('/api/Rules/properties', headers);
        const responseConditions = await axios.get('/api/Rules/conditions/groups', headers);
        return { segmentations: responseSegmentations?.data?.segmentaciones, properties: responseProperties?.data, conditions: responseConditions?.data }
    } catch (err) {
        // Handle Error Here
        console.error(err);
        return { segmentations: [], properties: {} }
    }

};

const AuthService = {
    login,
    loginWithMsal,
    logout
};

export default AuthService;
